import { SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT, type SparkyFeatureFlagProviderConfig } from '../extpoints'
import { FAWKES_APP_ID, FF_SPARKY_USE_SCM_NAV_V3, GOV_REGION, PROP_FAWKES_FEATURES, RUNNING, SASE_CN_CLIENT_ID } from '../framework/constants'
import { isInSilentLoginIframe } from '../init'
import { __info, __isLocalhost } from '../init/debug'
import type { ExtensionsConfig } from '../framework/microAppTypes'
import type { FeatureInfo } from '../framework/stateTypes'

const getFlagFromLocalStorage = (name: string, trueVal: string = 'on', falseVal: string = 'off', defaultVal?: boolean): boolean | undefined => {
  const localOverrideFlag = localStorage.getItem(name)
  return localOverrideFlag === trueVal ? true : localOverrideFlag === falseVal ? false : defaultVal
}

const FAWKES_FEATURES_REGEX = /^(?:fawkes\.features\.|main\.featuresInfo\.)/
const featureFlagProviderExts = {
  point: SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT,
  configs: [{
    id: 'sparky.ff.override.scm_nav_v3',
    config: {
      match: FF_SPARKY_USE_SCM_NAV_V3,
      fromService: 'ffs-sparky-override',
      getFeatureFlag: (name, _options, state) => {
        const { fxVars = {}, auth } = state
        if (fxVars.hub || fxVars.aiops_free || !fxVars.load_nav_extensions || fxVars.pdf_mode ||
          fxVars.oidc_client?.client_id === SASE_CN_CLIENT_ID || isInSilentLoginIframe) {
          return false
        }
        if (!fxVars.hub && !fxVars.aiops_free && !isInSilentLoginIframe) {
          const localBetaOverride = getFlagFromLocalStorage('override_use_scm_nav_v3_beta', 'force-on', 'opt-out')
          if (localBetaOverride === false) {
            console.info('Turn off Nav v3 locally')
            return false
          }
          const useLocalOverride = !fxVars.production && __isLocalhost && fxVars.use_scm_nav_v3_local !== undefined
          const localOverride = Boolean(useLocalOverride && fxVars.use_scm_nav_v3_local)
          if (fxVars.msp) { // override for msp
            if (localBetaOverride === true && !fxVars.disable_nav_v3) {
              console.info('Turn on Nav v3 locally')
              return true
            }
            const mspKey = 'sparky.framework.use_msp_nav_v3'
            if (auth?.feature_flags?.has(mspKey)) {
              const flag = auth.feature_flags.getValue<boolean>(mspKey)
              return Boolean(flag)
            }
            if (useLocalOverride) {
              __info(`Using local override value for feature flag "${name}" in MSP: ${localOverride}`)
            }
            return localOverride
          }
          if (localBetaOverride === true && !fxVars.disable_nav_v3) {
            console.info('Turn on Nav v3 locally')
            return true
          }
          if (useLocalOverride) {
            __info(`Using local override value for feature flag "${name}": ${localOverride}`)
            return localOverride
          }
          if (fxVars.disable_nav_v3) {
            return false
          }
          if (auth?.feature_flags?.has(name)) {
            const flag = auth.feature_flags.getValue<boolean>(name)
            if (flag) {
              if (auth.instances?.platform_region === GOV_REGION) {
                return Boolean(fxVars.use_scm_nav_v3_fedmod ||
                  auth.feature_flags.getValue<boolean>(`${name}_fedmod`)) // override for fedmod
              }
              const fawkesInst = auth.instances?.get(FAWKES_APP_ID)
              const fawkesVersion = fawkesInst?.status === RUNNING ? fawkesInst.runtime_attributes?.version : undefined
              if (fawkesVersion && !(fxVars.production ? fawkesVersion.startsWith('2025.r') : fawkesVersion.startsWith('2025.r') || fawkesVersion.startsWith('2024.r6'))) {
                console.warn(`Nav v3 is disabled due to Fawkes version "${fawkesVersion}" of this tenant ${auth.tsg_id} is not supported`)
                return false // override if fawkes version not ready
              }
            }
            return Boolean(flag)
          }
        }
        return undefined // default to undefined to avoid cached
      },
      allowFallback: false,
    }
  }, {
    id: 'sparky.ff.ffs',
    config: {
      fromService: ['ffs', 'feature-flag-service'],
      getFeatureFlag: (name, _options, state) => {
        const featureFlagService = state.auth?.feature_flags
        if (featureFlagService?.has(name)) {
          const value = featureFlagService.getValue(name)
          return value
        } // no need to care default value as allowFallback is on
      },
      allowFallback: true,
      priority: 1,
    }
  }, {
    id: 'fawkes.features-info',
    config: {
      fromService: ['fawkes.features', 'main.featuresInfo'],
      match: FAWKES_FEATURES_REGEX,
      getFeatureFlag: (name, _options, state) => {
        const features = state.main?.featuresInfo ||
          state.auth?.properties?.get(PROP_FAWKES_FEATURES)?.toggles?.map(
            ({ fn, name = fn, t, toggle = t }: any) => ({ name, toggle })
          )
        if (!features?.length) {
          return undefined
        }
        const checkIsFeatureEnabled = (featureList: FeatureInfo[] = [], featureKey = '') => {
          return featureList.some(feature => feature?.name === featureKey && feature.toggle === 1)
        }
        const feature = checkIsFeatureEnabled(features, name.replace(FAWKES_FEATURES_REGEX, ''))
        return feature
      },
      allowFallback: false,
    }
  }]
} as ExtensionsConfig<SparkyFeatureFlagProviderConfig>

export default featureFlagProviderExts
